import { useState, useEffect, useMemo } from "react";
import { useUserContext } from "./UserContext";
import { Cliente } from "../models/ClienteModel";
import { Producto } from "../models/ProductoModel";
import { LineaVenta } from "../models/LineaVentaModel";
import { fetchGetClientes } from "../services/clienteService";
import { fetchGetProductosParaVenta } from "../services/productoService";
import { isAbortError } from "../services/fetchService";
import { useNotification } from "./NotificationContext";


export const useVentaPresupuesto = () => {
  const { getUser, user } = useUserContext();
  const { showNotification } = useNotification();
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [idLocal, setIdLocal] = useState<string | undefined>(undefined);
  const [productos, setProductos] = useState<Producto[]>([]);
  const [selectDialogOpen, setSelectDialogOpen] = useState<boolean>(false);
  const [clienteSeleccionado, setClienteSeleccionado] = useState<Cliente | undefined>(undefined);
  const [productoSeleccionado, setProductoSeleccionado] = useState<Producto | undefined>(undefined);
  const [lineaVenta, setLineaVenta] = useState<LineaVenta[]>([]);
  const [data, setDialogData] = useState<any[]>([]);
  const [seleccionandoProducto, setSeleccionandoProducto] = useState<boolean>(false);
  const [seleccionandoCliente, setSeleccionandoCliente] = useState<boolean>(false);
  const [loadingProductos, setLoadingProductos] = useState(false);

  const userLoggedIn = useMemo(() => user ?? getUser(), [getUser, user]);

  useEffect(() => {
    if (userLoggedIn) {
      const controller = new AbortController();
      fetchGetClientes(userLoggedIn!, controller.signal).then((clis: Cliente[]) => {
        setClientes(clis);
      }).catch(err => {
        if (isAbortError(err)) return;
        console.log(err);
      })

      return () => controller.abort();
    }
  }, [userLoggedIn]);

  useEffect(() => {
    if (userLoggedIn && idLocal) {
      const controller = new AbortController();
      setLoadingProductos(true);
      fetchGetProductosParaVenta(idLocal, userLoggedIn!, controller.signal)
        .then((prods: Producto[]) => {
          setProductos(prods);
        })
        .catch(err => {
          if (isAbortError(err)) return;
          console.log(err);
          showNotification("Error al cargar productos", "error");
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoadingProductos(false);
        });

      return () => controller.abort();
    }
  }, [idLocal, showNotification, userLoggedIn]);


  const handleSelectClienteDialog = () => {
    setSeleccionandoProducto(false);
    setSeleccionandoCliente(true);
    setDialogData(clientes);
    setSelectDialogOpen(!selectDialogOpen);
  }

  const handleSelectProductoDialog = () => {
    if (!idLocal) {
      showNotification("Debe seleccionar un local primero", "error");
      return;
    }

    if (productos.length === 0) {
      showNotification("Cargando productos...", "info");
      return;
    }

    setSeleccionandoCliente(false);
    setSeleccionandoProducto(true);
    setDialogData(productos);
    setSelectDialogOpen(!selectDialogOpen);
  }

  const eventHandler = (params: any) => {
    try {
      if (seleccionandoCliente) {
        const row = params.row as Cliente;
        setClienteSeleccionado(row);
      }
      if (seleccionandoProducto) {
        const row = params.row as Producto;
        setProductoSeleccionado(row);
        setLineaVenta([...lineaVenta, { producto: { ...row }, cantidad: 1 }])
      }
      setSelectDialogOpen(false);
    } catch { }
  }

  const handleDeleteRow = (rowIndex: number) => {
    const updatedLineaVenta = [...lineaVenta];
    updatedLineaVenta.splice(rowIndex, 1);
    setLineaVenta(updatedLineaVenta);
  };

  const handleLocalChange = (idLocal: string | undefined) => {
    setIdLocal(idLocal);
  };

  const resetForm = () => {
    setLineaVenta([]);
    setClienteSeleccionado(undefined);
  };

  const showMessage = (message: string, severityLevel: "success" | "info" | "warning" | "error") => {
    showNotification(message, severityLevel);
  };

  return {
    // State
    clientes,
    idLocal,
    productos,
    selectDialogOpen,
    clienteSeleccionado,
    productoSeleccionado,
    lineaVenta,
    data,
    seleccionandoProducto,
    seleccionandoCliente,
    loadingProductos,
    userLoggedIn,
    // Functions
    handleSelectClienteDialog,
    handleSelectProductoDialog,
    eventHandler,
    handleDeleteRow,
    handleLocalChange,
    resetForm,
    showMessage,
    setSelectDialogOpen,
  };
};

